import React from 'react';
import styled from 'styled-components';
import { TrendingUp } from 'lucide-react';

interface CashFlowTrend {
  monthOffset: number;
  monthName: string;
  monthDate: string;
  actualAmount: number;
  forecastAmount: number;
  isForecast: boolean;
}

interface Props {
  data: CashFlowTrend[];
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
`;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  padding: 20px 0;

  @media (max-width: 767px) {
    height: 300px;
    overflow-x: auto;
  }
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 16px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    gap: 16px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-secondary);
`;

const LegendLine = styled.div<{ color: string; $isDashed?: boolean }>`
  width: 30px;
  height: 3px;
  background-color: ${props => props.color};
  ${props =>
    props.$isDashed &&
    `
    background-image: linear-gradient(to right, ${props.color} 50%, transparent 50%);
    background-size: 10px 3px;
    background-repeat: repeat-x;
  `}
`;

const LegendDot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const Tooltip = styled.div<{ x: number; y: number; $show: boolean }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  font-size: 13px;
  pointer-events: none;
  opacity: ${props => (props.$show ? 1 : 0)};
  transition: opacity 0.2s ease;
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 14px;
`;

const TooltipValue = styled.div`
  direction: ltr;
  text-align: right;
  color: #5a9070;
  font-weight: 700;
`;

const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const CashFlowTrendChart: React.FC<Props> = ({ data }) => {
  const [tooltip, setTooltip] = React.useState<{
    show: boolean;
    x: number;
    y: number;
    month: string;
    amount: number;
    type: string;
  }>({
    show: false,
    x: 0,
    y: 0,
    month: '',
    amount: 0,
    type: '',
  });

  const chartRef = React.useRef<SVGSVGElement>(null);

  // Calculate chart dimensions and scale
  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const chartWidth = 1000; // Base width
  const chartHeight = 350;
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Find max value for Y-axis scale
  const maxValue = Math.max(
    0,
    ...data.map(d => Math.max(d.actualAmount, d.forecastAmount))
  );

  // קפיצות "עגולות" (1/2/5 × 10^n) והסקאלה נקבעת לפי הקפיצה העליונה —
  // כך כל התוויות בתוך הגרף, גם כשהסכומים קטנים מ-10,000
  const yTicks = 5;
  const niceStep = (raw: number): number => {
    if (raw <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const normalized = raw / magnitude;
    const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return nice * magnitude;
  };
  const yTickInterval = niceStep(maxValue / yTicks);
  const yScale = innerHeight / (yTickInterval * yTicks);
  const yTickValues = Array.from(
    { length: yTicks + 1 },
    (_, i) => i * yTickInterval
  );

  // Calculate X positions for each month
  const xStep = innerWidth / (data.length - 1);
  const xOf = (i: number) => padding.left + i * xStep;
  const yOf = (amount: number) => padding.top + innerHeight - amount * yScale;

  // בפועל: כל החודשים עד החודש הנוכחי, כולל חודשים ללא הכנסה (הקו לא "קופץ" מעליהם)
  const isActualMonth = (d: CashFlowTrend) => d.monthOffset <= 0;
  // תחזית: מהחודש הנוכחי (הצפי שלו) והלאה
  const isForecastMonth = (d: CashFlowTrend) => d.monthOffset >= 0;

  const buildPath = (points: { x: number; y: number }[]) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  const actualPath = buildPath(
    data.map((d, i) => ({ d, i }))
      .filter(({ d }) => isActualMonth(d))
      .map(({ d, i }) => ({ x: xOf(i), y: yOf(d.actualAmount) }))
  );

  const forecastPath = buildPath(
    data.map((d, i) => ({ d, i }))
      .filter(({ d }) => isForecastMonth(d))
      .map(({ d, i }) => ({ x: xOf(i), y: yOf(d.forecastAmount) }))
  );

  const handleMouseMove = (
    e: React.MouseEvent<SVGCircleElement>,
    month: string,
    amount: number,
    type: string
  ) => {
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setTooltip({
        show: true,
        x: e.clientX - rect.left + 10,
        y: e.clientY - rect.top - 10,
        month,
        amount,
        type,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, show: false });
  };

  return (
    <Container>
      <TitleContainer>
        <TitleIcon>
          <TrendingUp size={20} />
        </TitleIcon>
        <Title>מגמת תזרים כספי</Title>
      </TitleContainer>
      <Subtitle>6 חודשים אחורה (בפועל) + 6 חודשים קדימה (תחזית)</Subtitle>

      <ChartContainer>
        <ChartSvg ref={chartRef} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
          {/* Grid lines */}
          {yTickValues.map((value, i) => {
            const y = padding.top + innerHeight - value * yScale;
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerWidth}
                  y2={y}
                  stroke="var(--border-color)"
                  strokeWidth="1"
                  strokeDasharray="4"
                />
                <text
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="var(--text-tertiary)"
                >
                  {formatCurrency(value)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {data.map((d, i) => {
            const x = padding.left + i * xStep;
            const shortMonth = d.monthName.split(' ')[0];
            return (
              <text
                key={i}
                x={x}
                y={padding.top + innerHeight + 20}
                textAnchor="middle"
                fontSize="11"
                fill="var(--text-secondary)"
              >
                {shortMonth}
              </text>
            );
          })}

          {/* Vertical divider at current month */}
          {data.findIndex(d => d.monthOffset === 0) !== -1 && (
            <line
              x1={padding.left + data.findIndex(d => d.monthOffset === 0) * xStep}
              y1={padding.top}
              x2={padding.left + data.findIndex(d => d.monthOffset === 0) * xStep}
              y2={padding.top + innerHeight}
              stroke="var(--warning-color)"
              strokeWidth="2"
              strokeDasharray="6"
              opacity="0.5"
            />
          )}

          {/* Actual amount line */}
          {actualPath && (
            <path
              d={actualPath}
              fill="none"
              stroke="#6b8cba"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.85"
            />
          )}

          {/* Forecast line */}
          {forecastPath && (
            <path
              d={forecastPath}
              fill="none"
              stroke="#5a9070"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="8 4"
              opacity="0.85"
            />
          )}

          {/* Data points */}
          {data.map((d, i) => {
            const x = xOf(i);
            const actualY = yOf(d.actualAmount);
            const forecastY = yOf(d.forecastAmount);

            return (
              <g key={i}>
                {isActualMonth(d) && (
                  <circle
                    cx={x}
                    cy={actualY}
                    r="5"
                    fill="#6b8cba"
                    stroke="white"
                    strokeWidth="2"
                    opacity="0.9"
                    style={{ cursor: 'pointer' }}
                    onMouseMove={(e) =>
                      handleMouseMove(e, d.monthName, d.actualAmount, 'בפועל')
                    }
                    onMouseLeave={handleMouseLeave}
                  />
                )}
                {isForecastMonth(d) && (
                  <circle
                    cx={x}
                    cy={forecastY}
                    r="5"
                    fill="#5a9070"
                    stroke="white"
                    strokeWidth="2"
                    opacity="0.9"
                    style={{ cursor: 'pointer' }}
                    onMouseMove={(e) =>
                      handleMouseMove(e, d.monthName, d.forecastAmount, 'תחזית')
                    }
                    onMouseLeave={handleMouseLeave}
                  />
                )}
              </g>
            );
          })}
        </ChartSvg>

        <Tooltip x={tooltip.x} y={tooltip.y} $show={tooltip.show}>
          <TooltipTitle>{tooltip.month}</TooltipTitle>
          <div>{tooltip.type}</div>
          <TooltipValue>{formatCurrency(tooltip.amount)}</TooltipValue>
        </Tooltip>
      </ChartContainer>

      <Legend>
        <LegendItem>
          <LegendLine color="#6b8cba" />
          <LegendDot color="#6b8cba" />
          <span>הכנסות בפועל</span>
        </LegendItem>
        <LegendItem>
          <LegendLine color="#5a9070" $isDashed />
          <LegendDot color="#5a9070" />
          <span>תחזית הכנסות</span>
        </LegendItem>
        <LegendItem>
          <LegendLine color="var(--warning-color)" $isDashed />
          <span>חודש נוכחי</span>
        </LegendItem>
      </Legend>
    </Container>
  );
};

export default CashFlowTrendChart;
